import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import type { SxProps, Theme } from '@mui/material/styles';

import { selectMenuItemRightSx, selectSelectedLabelSx } from '../selectMenuItemSx';

// A single-select has exactly one selected row, so a checkbox-style square
// on every option (as in AgeSelect's multi-select) would misleadingly
// suggest several could be picked — just the tick, no box, and only on the
// selected row. The empty slot below keeps every row's label aligned.
const checkSlotSx: SxProps<Theme> = { width: 18, flexShrink: 0, display: 'flex' };
const checkIconSx: SxProps<Theme> = (theme) => ({
  fontSize: 18,
  color: theme.palette.primary.main,
});

export interface SingleSelectOption {
  id: string;
  label: string;
}

interface SingleSelectProps {
  value: string;
  onChange: (id: string) => void;
  options: readonly SingleSelectOption[];
  sx?: SxProps<Theme>;
  variant?: 'standard' | 'outlined' | 'filled';
  displayEmpty?: boolean;
  /** Shown when nothing is selected (only with displayEmpty). */
  placeholder?: string;
  /**
   * Adds a disabled `placeholder` row at the top of the menu (in addition to
   * it showing as the empty-value render text) — used where the field also
   * needs an explicit "no value yet" item in the list itself.
   */
  showPlaceholderOption?: boolean;
  id?: string;
  ariaLabel?: string;
}

/**
 * Shared single-select for a flat `{id, label}` option list — amount and
 * location/state everywhere they're picked (the hero search, the madlib,
 * the impact map controls). Renders the selected row with the same
 * highlight treatment as `AgeSelect`, plus a plain tick (no checkbox
 * square) on the selected row, instead of each call site rendering its own
 * plain MUI `Select`.
 */
export function SingleSelect({
  value,
  onChange,
  options,
  sx,
  variant = 'standard',
  displayEmpty,
  placeholder,
  showPlaceholderOption,
  id,
  ariaLabel,
}: SingleSelectProps) {
  return (
    <Select
      id={id}
      value={value}
      variant={variant}
      displayEmpty={displayEmpty}
      aria-label={ariaLabel}
      onChange={(event) => onChange(event.target.value)}
      renderValue={(v) => {
        const selected = options.find((o) => o.id === v);
        return selected ? selected.label : (placeholder ?? '');
      }}
      sx={sx}
    >
      {showPlaceholderOption && (
        <MenuItem value="" disabled>
          {placeholder}
        </MenuItem>
      )}
      {options.map((option) => {
        const selected = value === option.id;
        return (
          <MenuItem key={option.id} value={option.id} sx={selectMenuItemRightSx}>
            <ListItemText
              primary={option.label}
              slotProps={{ primary: { sx: selected ? selectSelectedLabelSx : undefined } }}
            />
            <Box sx={checkSlotSx}>{selected && <CheckIcon sx={checkIconSx} />}</Box>
          </MenuItem>
        );
      })}
    </Select>
  );
}
